import { useSpinDelay } from 'spin-delay'
import { cn } from '@joyful/utils'
import type { ReactNode } from 'react'
import type { SpinningLoaderProps } from './SpinningLoader'
import { SpinningLoader } from './SpinningLoader'

export interface ErrorStateProps {
  children?: ReactNode
  className?: string
  error?: unknown
}

export function ErrorState({ children, className, error }: ErrorStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-2 rounded-md border-red-800/30 bg-red-800/10 p-4',
        className,
      )}
    >
      <p className="text-sm text-red-800">
        {error === undefined
          ? 'An error occurred.'
          : error instanceof Error
            ? error.message
            : typeof error === 'string'
              ? error
              : 'An error occurred.'}
      </p>
      {children}
    </div>
  )
}

export interface SpinDelayProps {
  readonly children: ReactNode

  /**
   * @default 200
   */
  readonly delayMs?: number

  /**
   * @default 100
   */
  readonly minDurationMs?: number
}

function SpinDelay({
  children,
  delayMs = 200,
  minDurationMs = 100,
}: SpinDelayProps) {
  const shouldShow = useSpinDelay(true, {
    delay: delayMs,
    minDuration: minDurationMs,
    ssr: false,
  })

  return shouldShow === true || delayMs === 0 ? children : null
}

export interface DelayedSpinnerProps
  extends
    Omit<SpinDelayProps, 'children'>,
    Pick<SpinningLoaderProps, 'height' | 'size'> {}

export function DelayedSpinner({
  height,
  size,
  ...props
}: DelayedSpinnerProps) {
  return (
    <SpinDelay {...props}>
      <SpinningLoader height={height} size={size} />
    </SpinDelay>
  )
}

export interface RawQueryStateProps {
  readonly children?: ReactNode
  readonly error?: ReactNode
  readonly isError: boolean
  readonly isPending: boolean
  readonly isSuccess: boolean
  readonly loading?: ReactNode
  readonly loadingOptions?: Omit<SpinDelayProps, 'children'>
}

const defaultError = <ErrorState />

export function RawQueryState({
  children,
  error = defaultError,
  isError,
  isPending,
  isSuccess,
  loading,
  loadingOptions,
}: RawQueryStateProps) {
  if (isPending === true) {
    return loading ?? <DelayedSpinner {...loadingOptions} />
  }

  if (isError === true) {
    return error
  }

  if (isSuccess === true) {
    return children
  }

  return null
}

export interface QueryStateQueryLike<E = Error> {
  readonly error: E
  readonly isError: boolean
  readonly isPending: boolean
  readonly isSuccess: boolean
}

export interface QueryStateProps<E = Error> extends Pick<
  RawQueryStateProps,
  'loading' | 'loadingOptions'
> {
  children?: ReactNode
  error?: (error: E) => ReactNode
  /**
   * @default query.isPending
   */
  isPending?: boolean

  query: QueryStateQueryLike<E>
}

const errorDefault = (err: unknown) => {
  return <ErrorState error={err} />
}

export function QueryState<E = Error>({
  children,
  error = errorDefault,
  isPending,
  loading,
  loadingOptions,
  query,
}: QueryStateProps<E>) {
  const resolvedIsPending = isPending ?? query.isPending
  return (
    <RawQueryState
      isPending={resolvedIsPending}
      isError={query.isError}
      isSuccess={query.isSuccess}
      loading={loading}
      loadingOptions={loadingOptions}
      error={query.isError === true ? error(query.error) : undefined}
    >
      {children}
    </RawQueryState>
  )
}

export interface QueriesStateProps<E = Error> extends Omit<
  QueryStateProps<E>,
  'query'
> {
  queries: Array<QueryStateQueryLike<E>>
}

export function QueriesState<E = Error>({
  children,
  error = errorDefault,
  loading,
  loadingOptions,
  queries,
}: QueriesStateProps<E>) {
  const isError = queries.some((query) => {
    return query.isError
  })
  const queryError = isError
    ? queries.find((query) => {
        return query.isError
      })?.error
    : undefined

  return (
    <RawQueryState
      isPending={queries.some((query) => {
        return query.isPending
      })}
      isError={isError}
      isSuccess={queries.every((query) => {
        return query.isSuccess
      })}
      loading={loading}
      loadingOptions={loadingOptions}
      error={queryError ? error(queryError) : undefined}
    >
      {children}
    </RawQueryState>
  )
}
